import { useCallback, useState } from "react";
import { Drawer } from "@design-system/Drawer";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { SchedulesReportOpenScheduleParams } from "@components/accounting/schedulesReport/schedulesReportDrillDown.lib";
import { ScheduleApplicationsTab } from "./ScheduleApplicationsTab";

export interface ScheduleApplicationsDrawerScope {
    key: number;
    params: SchedulesReportOpenScheduleParams;
}

interface ScheduleApplicationsDrawerProps {
    open: boolean;
    scope: ScheduleApplicationsDrawerScope | null;
    onClose: () => void;
}

const ScheduleApplicationsDrawerContent = ({
    params,
    onClose,
}: {
    params: SchedulesReportOpenScheduleParams;
    onClose: () => void;
}) => {
    const navigate = useStateAwareNavigate();
    const openReport = useCallback(
        (url: string) => {
            onClose();
            navigate(url);
        },
        [navigate, onClose],
    );
    const [templateId, setTemplateId] = useState(params.templateId ?? null);
    const [applicationId, setApplicationId] = useState(
        params.scheduleApplicationId ?? null,
    );
    const [applicationIds, setApplicationIds] = useState(
        params.applicationIds ?? [],
    );
    const [counterpartyName, setCounterpartyName] = useState(
        params.counterpartyName ?? null,
    );
    const [period, setPeriod] = useState({
        start: params.periodStart ?? null,
        end: params.periodEnd ?? null,
    });

    return (
        <ScheduleApplicationsTab
            templateId={templateId}
            onTemplateIdChange={setTemplateId}
            applicationId={applicationId}
            onApplicationIdChange={setApplicationId}
            applicationIds={applicationIds}
            onApplicationIdsClear={() => setApplicationIds([])}
            counterpartyName={counterpartyName}
            onCounterpartyNameChange={setCounterpartyName}
            periodStart={period.start}
            periodEnd={period.end}
            onPeriodChange={setPeriod}
            tabNavigationTop={null}
            viewCacheKey={
                WORKSPACE_USER_CACHE_KEYS.SCHEDULE_APPLICATIONS_DRAWER_VIEW_CACHE_KEY
            }
            isFullPage={false}
            onOpenReport={openReport}
        />
    );
};

export const ScheduleApplicationsDrawer = ({
    open,
    scope,
    onClose,
}: ScheduleApplicationsDrawerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const shouldRender = isOpen || open;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            onAnimationEnd={setIsOpen}
            direction="right"
            noPadding
            headerAddonLeft="Schedules"
        >
            {shouldRender && scope && (
                <ScheduleApplicationsDrawerContent
                    key={scope.key}
                    params={scope.params}
                    onClose={onClose}
                />
            )}
        </Drawer>
    );
};
